/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
} from "react";

import { Trans, useTranslation } from "react-i18next";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { FormProvider, Controller, useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import qs from "qs";
import clsx from "clsx";
import map from "lodash/map";
import some from "lodash/some";
import { getCode } from "country-list";
import {
  CountryCode,
  isValidPhoneNumber,
  parsePhoneNumber,
} from "libphonenumber-js/min";

import Grid, { GridProps } from "@mui/material/Grid";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Autocomplete from "@mui/material/Autocomplete";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import FormHelperText from "@mui/material/FormHelperText";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import RadioGroup from "@mui/material/RadioGroup";
import Radio from "@mui/material/Radio";
import { styled } from "@mui/material/styles";

import { motion } from "framer-motion";
import { MuiTelInput } from "mui-tel-input";
import useClickAway from "react-use/lib/useClickAway";
import { useWindowSize } from "react-use";

import Modal from "../../components/Modal";
import BackButton from "../../components/BackButton";
import VirtualKeyboard, {
  LayoutType,
  VirtualKeyboardRef,
} from "../../components/VirtualKeyboard";
import Curve from "../../components/Animations/PageTransitions/Curve";
import AggrementDialog from "../../components/Aggrement/AggrementDialog";
import { useAgreementState } from "../../lib/hooks/useAgreementState";
import { useAcceptedAgreements } from "../../context/Agreement";
import OfflineModal from "../../components/Offline/OfflineModal";
import MoveLeft from "../../components/Animations/MoveLeft";
import PhotoDialog from "../../components/Photo/PhotoDialog";

import {
  useClientPlan,
  useContactPreviewEnabled,
  useDigitalReceptionCountry,
  useDigitalReceptionMetadata,
  useDocumentScannerSettings,
  useLayout,
  usePhotoCaptureSettings,
  usePrintingEnabled,
  useAccessSettings,
  useTeamsVideoCallEnabled,
} from "../../lib/hooks/useQueryBoard";
import { useTimeoutFunction } from "../../lib/hooks/useTimeoutFunction";
import { useDebounceStatus } from "../../lib/hooks/useDebounceStatus";
import { useAccessControlAutoSwitch } from "../../lib/hooks/useAccessControlAutoSwitch";
import { useTeamsCallerTokenAndTargetTeamsUserIds } from "../../lib/hooks/useTeamsCallerTokenAndTargetTeamsUserIds";
import { foldDiacritics } from "../../lib/common/diacritics";

import { client } from "../../bit/board/hooks/useGraphQLClient";
import { useLanguage, useLanguages } from "../../bit/language";
// import usePageTracking from "../../bit/tracking/usePageTracking";
// import useEventTracking from "../../bit/tracking/useEventTracking";

import { isVirtualKeyboardEnabled } from "../../config/const";

import {
  QUERY_ACCESS_EVENT,
  QUERY_EMPLOYEES,
} from "../../config/graphql/query";
import { MUTATION_CREATE_GUEST } from "../../config/graphql/mutation";

import { useBrotherPrinter } from "../../context/BrotherPrinter";
import { useHeaderHeightContext } from "../../context/HeaderHeight";

const ManualCheckinGrid = styled(Grid)(({ theme }) => ({
  paddingTop: "3rem",
  flex: "0 1 auto",
  minHeight: 0,
  overflowY: "auto",
  alignContent: "flex-start",
  WebkitOverflowScrolling: "touch",

  "&::-webkit-scrollbar": {
    width: "2rem",
  },

  "&::-webkit-scrollbar-track": {
    background: theme.palette.primary.light,
    borderRadius: "8px",
  },

  "&::-webkit-scrollbar-thumb": {
    background: theme.palette.primary.main,
    borderRadius: "8px",
  },
}));

const ManualCheckinField = styled(motion.div)(({ theme }) => ({
  marginBottom: "1.75rem",
  width: "56.5rem",

  "@media screen and (orientation: landscape)": {
    width: "50rem",
  },
}));

interface FieldValues {
  info: { [key: string]: string | boolean };
  contact?: {
    id?: string;
    label?: string;
  };
  photo?: string;
}

interface CreateGuestInput {
  info: { [key: string]: string | boolean };
  employee?: string;
  screen?: string;
  acceptedAgreements?: string[];
  photo?: string;
}

const getFieldType = (
  code: string,
  fields: IManualRegistrationField[],
): ManualRegistrationFieldType | undefined =>
  code === "contact"
    ? "EmployeeList"
    : fields.find((field) => field.name === code || field.code === code)?.type;

const ManualCheckin = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const { pathname, state: locationState } = useLocation();
  const { print } = useBrotherPrinter();

  const { height: headerHeight } = useHeaderHeightContext();
  const { width, height } = useWindowSize();

  const mainRoute = /^\/access\/checkin\/manual\/?$/.test(pathname);

  const [language] = useLanguage();
  const languages = useLanguages();

  const { integratedManualCheckin, independentManualCheckin } = useLayout();

  const registrationFields: IManualRegistrationField[] = useMemo(() => {
    if (integratedManualCheckin?.enabled) {
      return integratedManualCheckin?.registration?.fields ?? [];
    }
    if (independentManualCheckin?.enabled) {
      return independentManualCheckin?.registration?.fields ?? [];
    }
    return [];
  }, [integratedManualCheckin, independentManualCheckin]);

  const hasContact = useMemo(
    () => registrationFields.some((field) => field.type === "EmployeeList"),
    [registrationFields],
  );

  const receptionMetadata = useDigitalReceptionMetadata();

  const contactPreviewEnabled = useContactPreviewEnabled();

  const {
    isAccessAllowedForGuestsForCheckin,
    isAccessEnabledForAutoOpenForCheckin,
    accessController,
  } = useAccessSettings();

  const { trigger: triggerAutoSwitch, doorModal } =
    useAccessControlAutoSwitch();

  const { isDocumentScannerEnabled, fields } = useDocumentScannerSettings();

  const getFieldDisabledStatus = (controllerId: string) => {
    if (!isDocumentScannerEnabled) {
      return false;
    }

    const scannerField = Object.values(fields).find(
      (f) => f.field === controllerId,
    );

    return (
      !!scannerField &&
      scannerField.field != null &&
      scannerField.allowManualInput === false
    );
  };

  const defaultFormValues: Partial<FieldValues> = useMemo(
    () => ({
      info: registrationFields.reduce(
        (acc: { [key: string]: string | boolean }, item) => {
          acc[item.name || item.code] = item.type !== "Checkbox" ? "" : false;

          if (locationState?.[item.name || item.code]) {
            acc[item.name || item.code] =
              locationState?.[item.name || item.code];
          }

          return acc;
        },
        {},
      ),
      contact: {
        label: "",
      },
    }),
    [registrationFields, locationState],
  );

  const [customOtherValue, setCustomOtherValue] = useState<string>();
  const [isOtherSelected, setIsOtherSelected] = useState<boolean>(false);

  const drCountry = useDigitalReceptionCountry();

  const drCountryCode = useMemo(
    () => (drCountry ? getCode(drCountry) : null),
    [drCountry],
  );

  const defaultCountry: CountryCode | undefined = useMemo(() => {
    if (!!drCountryCode) {
      return drCountryCode as CountryCode;
    }

    if (["nl", "fr", "de", "pt", "es", "pl", "it"].includes(language)) {
      return language.toUpperCase() as CountryCode;
    }
    if (language === "en") {
      return "GB";
    }
    if (language === "sr") {
      return "RS";
    }
    if (language === "en_US") {
      return "US";
    }
    if (language === "ja") {
      return "JP";
    }
    return undefined;
  }, [language]);

  const preferredCountries = useMemo(() => {
    return (drCountryCode ? [drCountryCode as CountryCode] : []).concat(
      languages.flatMap((language) => {
        if (["nl", "fr", "de", "pt", "es", "pl", "it"].includes(language)) {
          return [language.toUpperCase()];
        }
        if (language === "en") {
          return "GB";
        }
        if (language === "sr") {
          return ["RS"];
        }
        if (language === "en_US") {
          return ["US"];
        }
        if (language === "ja") {
          return ["JP"];
        }

        return [];
      }) as CountryCode[],
    );
  }, [languages]);

  const clientPlan = useClientPlan();

  const { screen, showFirst, agreementsFor } = useAgreementState();
  const { accepted, accept } = useAcceptedAgreements();

  /**
   * Manual registration always creates a Guest, so the audience is known from
   * the start here — no need to resolve anyone's identity first.
   */
  const guestAgreements = agreementsFor("Guest");

  const showsAgreementsUpfront = showFirst && !pathname.includes("contact");

  /**
   * Set when the guest submits with documents still outstanding — the mode
   * where they come after the form rather than before it.
   */
  const [agreementsRequested, setAgreementsRequested] = useState(false);

  /**
   * Derived, never copied into state. `agreementsFor` already drops whatever
   * has been accepted, while the board query hands back a new object on every
   * poll — and an undefined one whenever the kiosk's connection blips, since
   * `online` is part of its cache key. A copy kept here would be re-seeded off
   * that and reopen documents the guest is long done with, which is how a
   * finished check-in ended up back on an agreement instead of the home screen.
   */
  const pendingAgreements =
    showsAgreementsUpfront || agreementsRequested ? guestAgreements : [];

  /**
   * What the mutation reports as signed. Read through a ref because the submit
   * path runs from callbacks that deliberately hold no dependencies, and a
   * stale empty list there would record nothing at all.
   */
  const acceptedRef = useRef<string[]>(accepted);
  acceptedRef.current = accepted;

  const {
    isPhotoCaptureAllowedForVisitors,
    isPhotoCaptureRequiredForVisitors,
  } = usePhotoCaptureSettings();
  const [photoRequired, setPhotoRequired] = useState<boolean>(false);

  const [employeeSelectOpen, setEmployeeSelectOpen] = useState<boolean>(false);
  const [calling, setCalling] = useState<boolean>(false);

  const queryClient = useQueryClient();

  const printingEnabled = usePrintingEnabled();

  // usePageTracking({
  //   page: "manual_checkin",
  // });

  // const { trackEvent } = useEventTracking();

  const { data: employeesData } = useQuery(
    [
      "employees",
      {
        filter: {
          preferences: {
            visitor: {
              enabled: {
                EQ: true,
              },
            },
          },
        },
        pagination: {
          limit: 99999,
        },
      },
    ] as const,
    ({ queryKey: [_key, variables] }) =>
      client.request<{ employees: IMemberEmployee[] }>(
        QUERY_EMPLOYEES,
        variables,
      ),
    {
      enabled: hasContact,
    },
  );

  const employees = useMemo(() => employeesData?.employees, [employeesData]);

  const contactList = useMemo(
    () =>
      map(employees, (employee) => ({
        id: employee.id,
        label: employee.fullName,
      })),
    [employees],
  );

  const [layout, setLayout] = useState<LayoutType>("default-shift");

  const [focusedInput, setFocusedInput] = useState<string | undefined>(
    `info.${registrationFields[0]?.name || registrationFields[0]?.code}`,
  );

  const keyboard = useRef<VirtualKeyboardRef>(null);
  const keyboardBox = useRef<HTMLDivElement>(null);

  const methods = useForm<FieldValues>({
    defaultValues: defaultFormValues,
    mode: "onSubmit",
  });

  const { setValue, handleSubmit, watch } = methods;

  const selectedContact = watch("contact");

  const hasSelectedContact = !!selectedContact?.id;

  useEffect(() => {
    methods.register("photo");
  }, []);

  useEffect(() => {
    if (locationState && typeof locationState === "object") {
      Object.entries(locationState).forEach(([key, field]) => {
        if (
          field &&
          typeof field === "object" &&
          "id" in field &&
          "value" in field
        ) {
          const { id, value } = field as { id: string; value: any };

          const foundField = registrationFields.find((f) => f.id === id);

          if (foundField && value) {
            const fieldKey = foundField.name || foundField.code;

            setValue(`info.${fieldKey}` as any, value, {
              shouldValidate: false,
              shouldDirty: true,
              shouldTouch: true,
            });
          }
        }
      });
    }
  }, [locationState, setValue, registrationFields]);

  const {
    data,
    status: requestStatus,
    mutate,
    reset,
  } = useMutation((input: CreateGuestInput) => {
    return client.request<
      { createGuest: IAccessEvent },
      { input: CreateGuestInput }
    >(MUTATION_CREATE_GUEST, {
      input,
    });
  });

  const receiver = data?.createGuest?.receiver;
  const ownerId = data?.createGuest?.owner?.id;
  const receiverEmail = receiver?.email;

  const autoEmail = receiver?.availablePreferences?.visitor?.autoChannel?.email;
  const autoSMS = receiver?.availablePreferences?.visitor?.autoChannel?.sms;
  const autoCall = receiver?.availablePreferences?.visitor?.autoChannel?.call;
  const autoPing = receiver?.availablePreferences?.visitor?.autoChannel?.ping;
  const autoVideoCall =
    !!receiver?.availablePreferences?.visitor?.autoChannel?.videoCall &&
    !!data?.createGuest?.videoRoom;
  const autoTeamsCall =
    receiver?.availablePreferences?.visitor?.autoChannel?.teamsCall;

  const status = useDebounceStatus(requestStatus, 1500);

  const translationKey = useMemo(() => {
    const key = `manualCheckin:status.${status}`;

    if (status === "error") {
      return key;
    }

    if (status !== "success") {
      return `${key}.${hasSelectedContact ? "contact" : "no-contact"}`;
    }

    if (printingEnabled) {
      return `${key}.badge`;
    }

    let messageType = "no-email-sms";

    if (autoCall || autoPing) {
      messageType = "call-ping";
    } else if (autoTeamsCall) {
      messageType = "teams-call";
    } else if (autoVideoCall) {
      messageType = "video-call";
    } else if (autoEmail || autoSMS) {
      messageType = "email-sms";
    }

    return `${key}.${messageType}`;
  }, [
    status,
    autoCall,
    autoPing,
    autoEmail,
    autoSMS,
    autoTeamsCall,
    printingEnabled,
    hasSelectedContact,
    autoVideoCall,
  ]);

  const teamsVideoCallEnabled = useTeamsVideoCallEnabled();
  const { callerAcsToken, targetTeamsUserIds } =
    useTeamsCallerTokenAndTargetTeamsUserIds({
      emails: receiverEmail ? [receiverEmail] : [],
      enabled: !!autoTeamsCall && !!receiverEmail,
    });

  const onDismiss = useCallback(async () => {
    const id = data?.createGuest?.id;

    const isFree = clientPlan && clientPlan === "free";

    // const autoSMS = receiver?.availablePreferences?.visitor?.autoChannel?.sms;

    /**
     * Prefetch access event so we don't wait when on calling page
     */
    await queryClient
      .prefetchQuery([`access/event/${id}`], () =>
        client.request(QUERY_ACCESS_EVENT, { id }),
      )
      .catch(() => null);

    /**
     * Can the user be navigated to contact page based on permissions and cations
     *
     * - Has to be non free user
     * - Access must belong to a visitor that is checking in
     * - Employee has to have at least one notification channel enabled
     */
    const canContinue = !isFree && id && status === "success";

    if (canContinue) {
      if (ownerId) {
        let printWindow: Window | null = null;

        if (printingEnabled && receptionMetadata?.printRef === "window") {
          printWindow = window.open("", "");
          window.focus();
        }

        print(ownerId, printWindow);
      }

      if (autoCall || autoPing) {
        setCalling(true);

        return navigate(
          `/access/checkin/manual/contact/${id}/${
            !!autoCall ? "call" : "ping"
          }`,
          {
            replace: true,
          },
        );
      }

      if (!!autoTeamsCall) {
        return navigate(
          `/${
            teamsVideoCallEnabled
              ? "teams-video"
              : `access/checkin/manual/teams-audio`
          }-call?${qs.stringify({
            callerAcsToken,
            targetTeamsUserIds,
            type: "Guest",
          })}`,
          {
            replace: true,
          },
        );
      }

      if (autoVideoCall) {
        return navigate(
          `/video-call?${qs.stringify({
            id,
            type: "Guest",
          })}`,
          { state: { videoRoom: data?.createGuest?.videoRoom } },
        );
      }

      // if (!!autoSMS) {
      //   return navigate("/", { replace: true });
      // }

      if (
        some(
          receiver?.availablePreferences?.visitor?.channel,
          (v) => v === true,
        )
      ) {
        return navigate(`/contact/${id}`, { replace: true });
      }

      if (receptionMetadata?.enableExternalDoorTrigger) {
        return navigate("/door", { replace: true });
      }

      if (isAccessAllowedForGuestsForCheckin) {
        if (isAccessEnabledForAutoOpenForCheckin && accessController) {
          return triggerAutoSwitch(accessController);
        }
        return navigate(`/access-control/${accessController}`, {
          replace: true,
        });
      }
    }

    /**
     * Reset modal on error state
     */
    if (status === "error") {
      return reset();
    }

    return navigate("/", { replace: true });
  }, [status, data]);

  const timeout = useMemo(() => {
    if (status === "error") {
      return 10000;
    }

    if (status === "success") {
      return receptionMetadata?.message?.checkin?.durationMs || 3000;
    }

    return 0;
  }, [status]);

  useTimeoutFunction(onDismiss, timeout);

  const getLayoutForFieldAndValue = useCallback(
    (layout: LayoutType = "default", field = "", value = ""): LayoutType => {
      switch (field) {
        case "EmployeeList":
        case "Company":
        case "Text": {
          if (!value || value.slice(-1) === " ") {
            return "default-shift";
          }

          return "default";
        }

        case "Phone": {
          return "phone-pad";
        }

        case "Number": {
          return "pin";
        }

        case "Email": {
          return "email";
        }

        default: {
          return layout;
        }
      }
    },
    [],
  );

  const [showDocScanInvalid, setShowDocScanInvalid] = useState(false);

  useEffect(() => {
    if (locationState?.documentScanStatus === "invalid") {
      setShowDocScanInvalid(true);

      const timer = setTimeout(() => {
        setShowDocScanInvalid(false);
      }, 8000);

      return () => clearTimeout(timer);
    }

    return undefined;
  }, [locationState, navigate]);

  useEffect(() => {
    setLayout((prevLayout) =>
      getLayoutForFieldAndValue(prevLayout, registrationFields[0].type, ""),
    );
  }, [registrationFields]);

  const onKeyboardChange = useCallback(
    (value = "") => {
      const input = document.querySelector<HTMLInputElement>(
        `[name="${focusedInput}"]`,
      );

      let selectionStart = Math.max(0, value.length - 1);

      if (input && typeof input.selectionStart === "number") {
        selectionStart = input.selectionStart;
      }

      let prevValue = methods.getValues(focusedInput as any) || "";

      setLayout((prevLayout) =>
        getLayoutForFieldAndValue(
          prevLayout,
          getFieldType(
            focusedInput?.split(".").pop() || "Text",
            registrationFields,
          ),
          value,
        ),
      );

      if (focusedInput === "contact") {
        if (prevValue && typeof prevValue.label === "string") {
          prevValue = prevValue.label;
        }

        setValue("contact", { label: value });
      } else {
        setValue(focusedInput as any, value);
      }

      /**
       * Update caret position of text input
       */
      if (input) {
        const position = prevValue.length - value.length;

        const next = Math.max(0, selectionStart - position);

        const type = getFieldType(
          focusedInput?.split(".").pop() || "Text",
          registrationFields,
        );
        if (type === "Phone" || type === "Number") {
          keyboard.current?.setCaretPosition(
            prevValue.length < value.length
              ? prevValue.length + 1
              : value.length,
            prevValue.length < value.length
              ? prevValue.length + 1
              : value.length,
          );
        } else {
          input.setSelectionRange(next, next);
        }
      }
    },
    [focusedInput],
  );

  const onKeyboardKeyPress = useCallback(
    (button = "") => {
      if (button === "{alt}") {
        setLayout(
          (layout) =>
            (layout.endsWith("alt")
              ? layout.split("-").shift()
              : `${layout.split("-").shift()}-alt`) as LayoutType,
        );
      }

      if (button === "{shift}") {
        setLayout(
          (layout) =>
            (layout.endsWith("shift")
              ? layout.split("-").shift()
              : `${layout.split("-").shift()}-shift`) as LayoutType,
        );
      }
    },
    [focusedInput, layout],
  );

  useClickAway(keyboardBox, (event) => {
    if (
      event.target &&
      ["INPUT", "TEXTAREA"].includes((event.target as HTMLElement)?.tagName)
    ) {
      return;
    }

    setFocusedInput(undefined);
  });

  const onInputFocus = useCallback(
    (
      event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>,
    ) => {
      const {
        target: { name, value },
      } = event;
      // trackEvent({ type: "input_focus", value: `visitor_${name}` });

      /**
       * Skip if there is no virtual keyboard
       */

      if (!keyboard.current) {
        return;
      }

      setLayout((prevLayout) =>
        getLayoutForFieldAndValue(
          prevLayout,
          getFieldType(name?.split(".").pop() || "Text", registrationFields),
          value,
        ),
      );

      setFocusedInput(name);
    },
    [],
  );

  const onSubmit = useCallback(
    ({ info, contact, photo }: FieldValues) => {
      // trackEvent({ type: "notify_button_click", value: "visitor" });

      let input: CreateGuestInput = {
        info: Object.fromEntries(
          Object.entries(info)
            .filter(([key, value]) => value !== "")
            .map(([key, value]) => {
              const type = getFieldType(key, registrationFields);
              if (type === "Phone") {
                return [key, parsePhoneNumber(value as string).number];
              }
              return [key, value];
            }),
        ),
        employee: contact?.id,
        ...(screen ? { screen } : {}),
      };

      const acceptedAgreements = acceptedRef.current;

      if (acceptedAgreements.length) {
        input = {
          ...input,
          acceptedAgreements,
        };
      }

      if (photo) {
        input = {
          ...input,
          photo,
        };
      }

      mutate(input, {
        onSuccess() {
          // trackEvent({ type: "manual_checkin", value: "success" });
        },
        onError() {
          // trackEvent({ type: "manual_checkin", value: `error` });
        },
      });
    },
    [mutate],
  );

  const onBeforePhotoSubmit = useCallback(
    (variables: FieldValues) => {
      if (isPhotoCaptureAllowedForVisitors) {
        return setPhotoRequired(true);
      }

      return onSubmit(variables);
    },
    [isPhotoCaptureAllowedForVisitors],
  );

  const onBeforeAgreementSubmit = useCallback(
    (variables: FieldValues) => {
      /**
       * Anything still outstanding is shown now — including the case where the
       * documents were meant to come up front but this page was reached past
       * them. Nothing is reported as signed that the guest never saw.
       */
      if (guestAgreements.length > 0) {
        return setAgreementsRequested(true);
      }

      return onBeforePhotoSubmit(variables);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [guestAgreements],
  );

  /**
   * One list, kept by the provider, so every page of a check-in agrees on what
   * has been signed and a document cannot come back once it is done.
   */
  const onAcceptAgreement = accept;

  /**
   * The documents came after the form, so the check-in carries on by itself
   * once the last one is out of the way.
   *
   * `screen` stands in for "the board config is here": without it the list of
   * documents reads as empty for the wrong reason, and the check-in would go
   * out unsigned — and without a screen for the server to check it against.
   */
  useEffect(() => {
    if (!agreementsRequested || !screen || guestAgreements.length > 0) {
      return;
    }

    setAgreementsRequested(false);

    methods.handleSubmit(onBeforePhotoSubmit)();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agreementsRequested, screen, guestAgreements.length]);

  const onAcceptPhoto = useCallback((photo: string) => {
    methods.setValue("photo", photo);
    setPhotoRequired(false);
    methods.handleSubmit(onSubmit)();
  }, []);

  const onPhotoDialogClose = useCallback(() => {
    setPhotoRequired(false);

    if (!isPhotoCaptureRequiredForVisitors) {
      methods.handleSubmit(onSubmit)();
    }
  }, [isPhotoCaptureRequiredForVisitors]);

  /**
   * Synchronize text caret position with virtual keyboard
   */
  const onInputSelect = useCallback(
    (event: React.SyntheticEvent<HTMLInputElement, Event>) => {
      if (!keyboard.current) {
        return;
      }

      const { selectionStart, selectionEnd } = event.target as HTMLInputElement;

      if (
        !(
          typeof selectionStart === "number" && typeof selectionEnd === "number"
        )
      ) {
        return;
      }

      keyboard.current.setCaretPosition(selectionStart, selectionEnd);
    },
    [],
  );

  /**
   * Synchronize text input with virtual keyboard
   */
  const onInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const {
        target: { name, value, selectionStart, selectionEnd },
      } = event;
      /**
       * Skip if there is no virtual keyboard
       */
      if (!keyboard.current) {
        return;
      }

      setLayout((prevLayout) =>
        getLayoutForFieldAndValue(
          prevLayout,
          getFieldType(name?.split(".").pop() || "Text", registrationFields),
          value,
        ),
      );

      keyboard.current.setInput(value, name);
      keyboard.current.setCaretPosition(selectionStart, selectionEnd);
    },
    [],
  );

  const onCountryInputChange = useCallback((value: string) => {
    if (!keyboard.current) {
      return;
    }
    setLayout((prevLayout) =>
      getLayoutForFieldAndValue(
        prevLayout,
        getFieldType("Phone", registrationFields),
        value,
      ),
    );
    keyboard.current.setInput(value, "phone");
    keyboard.current.setCaretPosition(value.length, value.length);
    setValue("info.phone", value);
    setFocusedInput("info.phone");
  }, []);

  const typeMessages =
    status === "success" && receptionMetadata?.message?.checkin?.Guest;

  const localizedTitle =
    typeof typeMessages === "object" &&
    typeof typeMessages.title?.[language] === "string"
      ? typeMessages.title[language]
      : undefined;

  const localizedMessage =
    typeof typeMessages === "object" &&
    typeof typeMessages.message?.[language] === "string"
      ? typeMessages.message[language]
      : undefined;

  const mcOverrides = receptionMetadata?.uiOverrides?.manualCheckin;

  const titleOverride = mcOverrides?.title?.[language];
  const subtitleOverride = mcOverrides?.subtitle?.[language];
  const btnOverride = mcOverrides?.btn?.[language];

  const renderDocumentScannerSubtitle = (
    subtitleValue:
      | {
          parts: {
            text: string;
            bold?: boolean;
          }[];
        }[]
      | string,
  ) => {
    if (Array.isArray(subtitleValue)) {
      return (
        <ul
          style={{
            textAlign: "left",
          }}
        >
          {subtitleValue.map((item) => (
            <li key={item.parts.map((p) => p.text).join("-")}>
              {item.parts.map((part, partIndex) => {
                const partKey = `${part.text}-${
                  part.bold ? "bold" : "normal"
                }-${partIndex}`;
                return part.bold ? (
                  <span key={partKey} style={{ fontWeight: "bold" }}>
                    {part.text}
                  </span>
                ) : (
                  <span key={partKey}>{part.text}</span>
                );
              })}
            </li>
          ))}
        </ul>
      );
    }

    return subtitleValue;
  };

  return (
    <Curve>
      <FormProvider {...methods}>
        <div className="d-flex align-items-center flex-column h-100">
          <BackButton
            to={
              integratedManualCheckin?.enabled ? "/access/option/checkin" : "/"
            }
          />
          {!isDocumentScannerEnabled ? (
            <div className="mb-5 text-center" style={{ margin: "0 10rem" }}>
              <Typography variant="h1">
                {titleOverride || t("manualCheckin:title.default")}
              </Typography>
              <Typography variant="h3" className="font-weight-regular pt-3">
                {subtitleOverride || t("manualCheckin:subtitle.default")}
              </Typography>
            </div>
          ) : (
            <div className="mb-5 text-center" style={{ margin: "0 10rem" }}>
              <Typography variant="h1">
                {t("manualCheckin:title.documentScanner")}
              </Typography>
              <Typography
                variant="h3"
                className="font-weight-regular pt-3 text-center"
                sx={{ fontWeight: "normal" }}
              >
                {renderDocumentScannerSubtitle(
                  t("manualCheckin:subtitle.documentScanner", {
                    returnObjects: true,
                  }),
                )}
              </Typography>
            </div>
          )}

          <div
            style={{
              flex: "1 1 0",
              minHeight: 0,
              width: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <form
              className="form d-flex flex-column mt-0"
              style={{ flex: "1 1 0", minHeight: 0, width: "100%" }}
              autoComplete="off"
              onSubmit={handleSubmit(onBeforeAgreementSubmit)}
            >
              <ManualCheckinGrid
                container
                justifyContent="center"
                columnGap={4}
              >
                {registrationFields.map((field, i) => {
                  if (field.type === "Text" || field.type === "Number")
                    return (
                      <MoveLeft
                        key={field.code}
                        disabled={!mainRoute}
                        delay={0.4 + i * 0.06}
                      >
                        <ManualCheckinField>
                          <Controller
                            name={`info.${field.name || field.code}`}
                            rules={{
                              ...(field.required && {
                                required: t("manualCheckin:error.required"),
                              }),
                            }}
                            render={({
                              field: { onChange, ref, value, ...rest },
                              fieldState: { error },
                            }) => {
                              const disabled = getFieldDisabledStatus(field.id);
                              return (
                                <TextField
                                  {...rest}
                                  error={!!error}
                                  helperText={error?.message ?? " "}
                                  type={field.type.toLowerCase()}
                                  autoFocus={i === 0}
                                  inputRef={ref}
                                  variant="outlined"
                                  label={
                                    field.label + (field.required ? " *" : "")
                                  }
                                  value={value}
                                  onSelect={onInputSelect}
                                  onChange={(event) => {
                                    const {
                                      target: { value },
                                    } = event;

                                    onInputChange(event);

                                    onChange(value);
                                  }}
                                  className={error && "error__field"}
                                  onFocus={onInputFocus}
                                  disabled={disabled}
                                  sx={(theme) => ({
                                    "& .MuiInputBase-input.Mui-disabled": {
                                      WebkitTextFillColor:
                                        theme.palette.text.primary,
                                      opacity: 1,
                                    },
                                    "& .MuiInputLabel-root.Mui-disabled": {
                                      color: theme.palette.text.primary,
                                      opacity: 0.7,
                                    },
                                  })}
                                />
                              );
                            }}
                          />
                        </ManualCheckinField>
                      </MoveLeft>
                    );

                  if (field.type === "Email") {
                    return (
                      <MoveLeft
                        key={field.code}
                        disabled={!mainRoute}
                        delay={0.4 + i * 0.06}
                      >
                        <ManualCheckinField>
                          <Controller
                            name={`info.${field.name || field.code}`}
                            rules={{
                              ...(field.required && {
                                required: t("manualCheckin:error.required"),
                              }),
                              pattern: {
                                value:
                                  /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/,
                                message: t("manualCheckin:error.email"),
                              },
                            }}
                            render={({
                              field: { onChange, ref, ...rest },
                              fieldState: { error },
                            }) => (
                              <TextField
                                {...rest}
                                error={!!error}
                                helperText={error?.message ?? " "}
                                inputRef={ref}
                                autoFocus={i === 0}
                                type={
                                  isVirtualKeyboardEnabled ? "text" : "email"
                                }
                                variant="outlined"
                                label={
                                  field.label + (field.required ? " *" : "")
                                }
                                value={rest.value || ""}
                                onSelect={onInputSelect}
                                onChange={(event) => {
                                  const {
                                    target: { value },
                                  } = event;

                                  onInputChange(event);

                                  onChange(value);
                                }}
                                className={error && "error__field"}
                                onFocus={onInputFocus}
                              />
                            )}
                          />
                        </ManualCheckinField>
                      </MoveLeft>
                    );
                  }

                  if (field.type === "Phone") {
                    return (
                      <MoveLeft
                        key={field.code}
                        disabled={!mainRoute}
                        delay={0.4 + i * 0.06}
                      >
                        <ManualCheckinField>
                          <Controller
                            name={`info.${field.name || field.code}`}
                            rules={{
                              ...(field.required && {
                                required: t("manualCheckin:error.required"),
                              }),
                              validate: (value) => {
                                return (
                                  !value ||
                                  isValidPhoneNumber(value) ||
                                  t("manualCheckin:error.phone")
                                );
                              },
                            }}
                            render={({
                              field: { ref, onChange, ...rest },
                              fieldState: { error },
                            }) => (
                              <MuiTelInput
                                {...rest}
                                error={!!error}
                                inputRef={ref}
                                variant="outlined"
                                autoFocus={i === 0}
                                helperText={error?.message ?? " "}
                                defaultCountry={defaultCountry}
                                preferredCountries={preferredCountries}
                                label={
                                  field.label + (field.required ? " *" : "")
                                }
                                value={rest.value || ""}
                                onSelect={(e: any) => {
                                  onInputChange(e);
                                  onChange(e.target.value);
                                }}
                                onChange={(input) => {
                                  onChange(input);
                                  onCountryInputChange(input);
                                }}
                                className={error && "error__field"}
                                onFocus={onInputFocus}
                              />
                            )}
                          />
                        </ManualCheckinField>
                      </MoveLeft>
                    );
                  }

                  if (field.type === "EmployeeList") {
                    return (
                      <MoveLeft
                        key={field.code}
                        disabled={!mainRoute}
                        delay={0.4 + i * 0.06}
                      >
                        <ManualCheckinField>
                          <Controller
                            name="contact"
                            rules={{
                              ...(field.required && {
                                required: {
                                  value: true,
                                  message: t("manualCheckin:error.required"),
                                },
                                validate: {
                                  exists: (value) => {
                                    if (!(value && value?.id)) {
                                      return t("manualCheckin:error.required");
                                    }

                                    return true;
                                  },
                                },
                              }),
                            }}
                            render={({
                              field: { onChange, name, value },
                              fieldState: { error },
                            }) => {
                              return (
                                <Autocomplete
                                  open={
                                    employeeSelectOpen &&
                                    (contactPreviewEnabled ||
                                      (!value?.id &&
                                        (value?.label?.length ?? 0) > 2) ||
                                      !!value?.id)
                                  }
                                  clearOnBlur
                                  blurOnSelect
                                  disableClearable
                                  disablePortal
                                  autoFocus={i === 0}
                                  noOptionsText={t("manualCheckin:noOption")}
                                  // PopperComponent={PopperTop}
                                  value={!!(value && value.id) ? value : null}
                                  options={contactList}
                                  getOptionLabel={(option) =>
                                    option.label || ""
                                  }
                                  isOptionEqualToValue={(option, value) =>
                                    value?.id === option?.id
                                  }
                                  filterOptions={(options) => {
                                    if (value?.id) {
                                      return options.filter(
                                        (o) => o.id === value.id,
                                      );
                                    }

                                    const query = foldDiacritics(
                                      value?.label ?? "",
                                    );

                                    if (contactPreviewEnabled) {
                                      if (!query) return options;

                                      return options.filter((o) =>
                                        foldDiacritics(o.label).includes(query),
                                      );
                                    }

                                    if (!query || query.length < 3) return [];

                                    return options.filter((o) =>
                                      foldDiacritics(o.label).includes(query),
                                    );
                                  }}
                                  renderOption={(props, option) => (
                                    <li {...props} key={option.id}>
                                      {option.label}
                                    </li>
                                  )}
                                  onChange={(_event, newValue, reason) => {
                                    if (reason === "selectOption") {
                                      onChange(newValue);
                                      // trackEvent({
                                      //   type: "contact_select",
                                      //   value: "visitor",
                                      // });
                                    }
                                  }}
                                  onBlur={() => {
                                    const currentValue = methods.getValues(
                                      name as any,
                                    );

                                    setEmployeeSelectOpen(false);

                                    if (!!(currentValue && currentValue.id)) {
                                      return;
                                    }

                                    if (keyboard.current) {
                                      keyboard.current.setInput("", name);
                                    }

                                    onChange(undefined);
                                  }}
                                  inputValue={
                                    !!(value && value.label) ? value.label : ""
                                  }
                                  renderInput={(params) => {
                                    return (
                                      <TextField
                                        {...params}
                                        error={!!error}
                                        helperText={error?.message ?? " "}
                                        type="text"
                                        name="contact"
                                        variant="outlined"
                                        autoCapitalize="sentences"
                                        label={
                                          field.label +
                                          (field.required ? " *" : "")
                                        }
                                        className={error && "error__field"}
                                        onChange={(event) => {
                                          const {
                                            target: { value },
                                          } = event;

                                          onInputChange(event);

                                          onChange({
                                            label: value,
                                          });
                                        }}
                                        onSelect={onInputSelect}
                                        inputProps={{
                                          ...params.inputProps,
                                          onFocus: (e) => {
                                            setEmployeeSelectOpen(true);
                                            onInputFocus(e);
                                          },
                                        }}
                                      />
                                    );
                                  }}
                                />
                              );
                            }}
                          />
                        </ManualCheckinField>
                      </MoveLeft>
                    );
                  }

                  if (field.type === "Dropdown") {
                    return (
                      <MoveLeft
                        key={field.code}
                        disabled={!mainRoute}
                        delay={0.4 + i * 0.06}
                      >
                        <ManualCheckinField>
                          <Controller
                            name={`info.${field.name || field.code}`}
                            rules={{
                              ...(field.required && {
                                required: t("manualCheckin:error.required"),
                              }),
                            }}
                            render={({
                              field: { onChange, name, value },
                              fieldState: { error },
                            }) => {
                              let height = "0";

                              if (field.dropdownOptions?.length) {
                                height = "24rem";

                                if (field.dropdownOptions.length === 2) {
                                  height = "17rem";
                                }

                                if (field.dropdownOptions.length === 1) {
                                  height = "9rem";
                                }
                              }
                              return (
                                <FormControl fullWidth error={!!error}>
                                  <InputLabel>
                                    {field.label + (field.required ? " *" : "")}
                                  </InputLabel>
                                  <Select
                                    value={value || ""}
                                    onChange={(event) =>
                                      onChange(event.target.value)
                                    }
                                    label={
                                      field.label + (field.required ? " *" : "")
                                    }
                                    MenuProps={{
                                      PaperProps: {
                                        sx: {
                                          marginTop: "1rem !important",
                                          height,
                                        },
                                      },
                                    }}
                                  >
                                    {field.dropdownOptions?.map((option) => (
                                      <MenuItem
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                  <FormHelperText>
                                    {error?.message ?? " "}
                                  </FormHelperText>
                                </FormControl>
                              );
                            }}
                          />
                        </ManualCheckinField>
                      </MoveLeft>
                    );
                  }

                  if (field.type === "RadioButtons") {
                    return (
                      <MoveLeft
                        key={field.code}
                        disabled={!mainRoute}
                        delay={0.4 + i * 0.06}
                      >
                        <ManualCheckinField>
                          <Controller
                            name={`info.${field.name || field.code}`}
                            rules={{
                              ...(field.required && {
                                required: t("manualCheckin:error.required"),
                              }),
                            }}
                            render={({
                              field: { onChange, name, value },
                              fieldState: { error },
                            }) => (
                              <FormControl fullWidth error={!!error}>
                                <Typography variant="h4" className="mb-2">
                                  {field.label + (field.required ? " *" : "")}
                                </Typography>
                                <RadioGroup
                                  name={name}
                                  value={value}
                                  onChange={(event) => {
                                    const selectedValue = event.target.value;

                                    onChange(selectedValue);
                                  }}
                                >
                                  {field.radioButtons?.buttons?.map(
                                    (option) => (
                                      <FormControlLabel
                                        key={option.value}
                                        value={option.value}
                                        onChange={() => {
                                          setIsOtherSelected(false);
                                          setCustomOtherValue("");
                                        }}
                                        control={
                                          <Radio
                                            sx={{ transform: "scale(2)" }}
                                          />
                                        }
                                        label={
                                          <span
                                            style={{
                                              fontSize: "2.5rem",
                                              marginLeft: "1rem",
                                            }}
                                          >
                                            {option.label}
                                          </span>
                                        }
                                        style={{
                                          marginLeft: 0,
                                          marginBottom: "0.75rem",
                                        }}
                                      />
                                    ),
                                  )}
                                  {field.radioButtons?.hasOther && (
                                    <>
                                      <FormControlLabel
                                        key="other"
                                        value={customOtherValue}
                                        control={
                                          <Radio
                                            sx={{ transform: "scale(2)" }}
                                          />
                                        }
                                        label={
                                          <span
                                            style={{
                                              fontSize: "2.5rem",
                                              marginLeft: "1rem",
                                            }}
                                          >
                                            {t(
                                              "manualCheckin:placeholder.other",
                                            )}
                                          </span>
                                        }
                                        style={{
                                          marginLeft: 0,
                                        }}
                                        onChange={() => {
                                          setIsOtherSelected(true);
                                          setCustomOtherValue("");
                                          onChange("");
                                        }}
                                      />
                                      {isOtherSelected && (
                                        <TextField
                                          fullWidth
                                          variant="outlined"
                                          placeholder="Enter your option"
                                          value={customOtherValue}
                                          onChange={(e) => {
                                            setCustomOtherValue(e.target.value);
                                            onChange(e.target.value);
                                          }}
                                          style={{
                                            marginTop: "1rem",
                                            fontSize: "2rem",
                                          }}
                                        />
                                      )}
                                    </>
                                  )}
                                </RadioGroup>
                                <FormHelperText>
                                  {error?.message ?? " "}
                                </FormHelperText>
                              </FormControl>
                            )}
                          />
                        </ManualCheckinField>
                      </MoveLeft>
                    );
                  }

                  if (field.type === "Checkbox") {
                    return (
                      <MoveLeft
                        key={field.code}
                        disabled={!mainRoute}
                        delay={0.4 + i * 0.06}
                      >
                        <ManualCheckinField>
                          <Typography variant="h4">{field.label}</Typography>
                          {field.checkbox?.description?.title && (
                            <Typography variant="h4" className="fs-3">
                              {field.checkbox.description.title}
                            </Typography>
                          )}
                          {field.checkbox?.description?.text && (
                            <p
                              style={{
                                fontSize: "1.85rem",
                              }}
                            >
                              {field.checkbox.description.text}
                            </p>
                          )}
                          <Controller
                            name={`info.${field.name || field.code}`}
                            render={({
                              field: { onChange, ...rest },
                              fieldState: { error },
                            }) => (
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    {...rest}
                                    defaultChecked={false}
                                    onChange={(event) => {
                                      onChange(event.target.checked);
                                    }}
                                    sx={{
                                      "& .MuiSvgIcon-root": {
                                        fontSize: "3rem",
                                      },
                                    }}
                                  />
                                }
                                label={field.checkbox?.label}
                                sx={{
                                  marginBottom: "2rem",
                                  "& .MuiFormControlLabel-label": {
                                    fontSize: "2.5rem",
                                  },
                                }}
                              />
                            )}
                          />
                        </ManualCheckinField>
                      </MoveLeft>
                    );
                  }

                  return (
                    <MoveLeft
                      key={field.code}
                      disabled={!mainRoute}
                      delay={0.4 + i * 0.06}
                    >
                      <ManualCheckinField>
                        <Controller
                          name={`info.${field.name || field.code}`}
                          rules={{
                            ...(field.required && {
                              required: t("manualCheckin:error.required"),
                            }),
                          }}
                          render={({
                            field: { onChange, ref, value, ...rest },
                            fieldState: { error },
                          }) => {
                            const disabled = getFieldDisabledStatus(field.id);

                            return (
                              <TextField
                                {...rest}
                                error={!!error}
                                helperText={error?.message ?? " "}
                                inputRef={ref}
                                autoFocus={i === 0}
                                variant="outlined"
                                label={
                                  field.label + (field.required ? " *" : "")
                                }
                                value={value}
                                onSelect={onInputSelect}
                                onChange={(event) => {
                                  const {
                                    target: { value },
                                  } = event;

                                  onInputChange(event);

                                  onChange(value);
                                }}
                                className={error && "error__field"}
                                onFocus={onInputFocus}
                                autoComplete="chrome-off"
                                disabled={disabled}
                              />
                            );
                          }}
                        />
                      </ManualCheckinField>
                    </MoveLeft>
                  );
                })}
              </ManualCheckinGrid>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <MoveLeft disabled={!mainRoute} delay={0.6}>
                  <motion.div
                    className="mt-5"
                    style={{ width: "56.5rem", paddingBottom: "5rem" }}
                  >
                    <Button
                      variant="contained"
                      className="w-100"
                      style={{
                        transform: "translateX(-1rem)",
                      }}
                      onClick={handleSubmit(onBeforeAgreementSubmit)}
                    >
                      {btnOverride || t("manualCheckin:btn")}
                    </Button>
                  </motion.div>
                </MoveLeft>
              </div>
            </form>
          </div>
          {status !== "idle" && !calling && (
            <Modal
              status={status}
              title={
                // @ts-ignore
                localizedTitle ?? <Trans i18nKey={`${translationKey}.title`} />
              }
              message={
                localizedMessage ?? (
                  // @ts-ignore
                  <Trans i18nKey={`${translationKey}.message`} />
                )
              }
              onDismiss={onDismiss}
            />
          )}
          {isVirtualKeyboardEnabled && (
            <div
              ref={keyboardBox}
              className={clsx("keyboard ", { keyboard__out: !focusedInput })}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <VirtualKeyboard
                ref={keyboard}
                layoutName={layout}
                inputName={focusedInput}
                onChange={onKeyboardChange}
                onKeyPress={onKeyboardKeyPress}
              />
            </div>
          )}
        </div>
      </FormProvider>
      {/* Once the check-in is away nothing about it is still owed, so a
          document can no longer land on top of the confirmation. */}
      <AggrementDialog
        aggrements={requestStatus === "idle" ? pendingAgreements : []}
        onDismiss={() => {
          return navigate("/");
        }}
        onAccept={onAcceptAgreement}
      />
      {photoRequired && (
        <PhotoDialog
          onClose={onPhotoDialogClose}
          onAccept={onAcceptPhoto}
          required={isPhotoCaptureRequiredForVisitors}
        />
      )}
      {showDocScanInvalid && (
        <Modal
          status="error"
          title={<Trans i18nKey="manualCheckin:documentScan.invalid.title" />}
          message={
            <Trans i18nKey="manualCheckin:documentScan.invalid.message" />
          }
          onDismiss={() => setShowDocScanInvalid(false)}
        />
      )}
      <Outlet />
      <OfflineModal />
      {doorModal}
    </Curve>
  );
};

export default ManualCheckin;
